import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient, withMinDelayThunk } from '../../utils/api/';

export const fetchTodoTags = createAsyncThunk(
  'todoTags/fetchTodoTags',
  withMinDelayThunk(async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/tags');
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch todo tags');
    }
  })
);

export const createTodoTag = createAsyncThunk(
  'todoTags/createTodoTag',
  withMinDelayThunk(async (name, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/tags', { name });
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create todo tag');
    }
  })
);

export const updateTodoTag = createAsyncThunk(
  'todoTags/updateTodoTag',
  withMinDelayThunk(async ({ tagId, name }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/tags/${tagId}`, { name });
      return response.data?.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update todo tag');
    }
  })
);

export const deleteTodoTag = createAsyncThunk(
  'todoTags/deleteTodoTag',
  withMinDelayThunk(async (name, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/tags/${encodeURIComponent(name)}`);
      return name;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete todo tag');
    }
  })
);

const initialState = {
  items: [],
  isFetching: false,
  error: null,
  status: 'idle',
};

const todoTagsSlice = createSlice({
  name: 'todoTags',
  initialState,
  reducers: {
    clearTodoTagError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTodoTags.pending, (state) => {
        state.isFetching = true;
        state.error = null;
        state.status = 'loading';
      })
      .addCase(fetchTodoTags.fulfilled, (state, action) => {
        state.isFetching = false;
        state.items = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchTodoTags.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(createTodoTag.pending, (state) => {
        state.error = null;
      })
      .addCase(createTodoTag.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.items.sort((left, right) => left.name.localeCompare(right.name));
        state.status = 'succeeded';
      })
      .addCase(createTodoTag.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(updateTodoTag.pending, (state) => {
        state.error = null;
      })
      .addCase(updateTodoTag.fulfilled, (state, action) => {
        state.items = state.items
          .map((tag) => (tag._id === action.payload._id ? action.payload : tag))
          .sort((left, right) => left.name.localeCompare(right.name));
        state.status = 'succeeded';
      })
      .addCase(updateTodoTag.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
      })
      .addCase(deleteTodoTag.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteTodoTag.fulfilled, (state, action) => {
        state.items = state.items.filter((tag) => tag.name !== action.payload);
        state.status = 'succeeded';
      })
      .addCase(deleteTodoTag.rejected, (state, action) => {
        state.error = action.payload;
        state.status = 'failed';
      });
  },
});

export const selectAllTodoTags = (state) => state.todoTags.items;
export const selectTodoTagsFetching = (state) => state.todoTags.isFetching;
export const selectTodoTagsError = (state) => state.todoTags.error;
export const selectTodoTagsStatus = (state) => state.todoTags.status;

export const { clearTodoTagError } = todoTagsSlice.actions;

export default todoTagsSlice.reducer;
